import { UserRole } from '@prisma/client';
import React from 'react';
import { IconType } from 'react-icons';
import { RouteComponentProps } from 'react-router-dom';
import { IS_DEV } from './config';
import icons from './icons';
import { LangKey } from './intl';
import { useAuthState } from './state/auth';
import { useIntl } from './state/intl';
import UsersUtils from './utils/users.utils';

export interface PMSRouteProps {
  path: string;
  menuLabel: string;
  menuIcon: IconType;
  exact?: boolean;
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
}

export type PMSRoutes = PMSRouteProps[];

const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const PropertiesPage = React.lazy(() => import('./pages/properties/PropertiesPage'));
const BookingsPage = React.lazy(() => import('./pages/bookings/BookingsPage'));
const AuctionsPage = React.lazy(() => import('./pages/auctions/AuctionsPage'));
const GuestsPage = React.lazy(() => import('./pages/guests/GuestsPage'));
const UsersPage = React.lazy(() => import('./pages/users/UsersPage'));
// const TasksPage = React.lazy(() => import('pages/tasks/TasksPage'));
const CustomersPage = React.lazy(() => import('./pages/customers/CustomersPage'));
// const RequestsPage = React.lazy(() => import('pages/booking-requests/BookingRequestsPage'));

export function useRoutes(): PMSRoutes {
  const { user } = useAuthState();
  const { lang, txc } = useIntl();

  const txs: {
    [key in LangKey]: {
      properties: string;
      bookings: string;
      guests: string;
      users: string;
      tasks: string;
    };
  } = {
    en: {
      properties: 'Properties',
      bookings: 'Bookings',
      guests: 'Guests',
      users: 'Users',
      tasks: 'Tasks',
    },
    it: {
      properties: 'Proprietà',
      bookings: 'Prenotazioni',
      guests: 'Ospiti',
      users: 'Personale',
      tasks: 'Attività',
    },
    es: {
      properties: 'Properties',
      bookings: 'Bookings',
      guests: 'Guests',
      users: 'Users',
      tasks: 'Tasks',
    },
  };

  const tx = txs[lang];

  const routes: PMSRoutes = [];

  if (UsersUtils.hasRoles(user, UserRole.ADMIN)) {
    routes.push({
      path: '/',
      menuLabel: 'Dashboard',
      menuIcon: icons.dashboard,
      exact: true,
      component: Dashboard,
    });
  }

  routes.push({
    path: '/properties',
    menuLabel: tx.properties,
    menuIcon: icons.properties,
    component: PropertiesPage,
    exact: true,
  });

  // Bookings
  if (
    UsersUtils.hasRoles(
      user,
      UserRole.ADMIN,
      UserRole.CUSTOMER_CARE,
      UserRole.OWNER,
      UserRole.RECEIVER,
      UserRole.CLEANER,
      UserRole.CITY_MANAGER
    )
  ) {
    routes.push({
      path: '/bookings',
      menuLabel: tx.bookings,
      menuIcon: icons.bookings,
      component: BookingsPage,
      exact: true,
    });
  }

  // TODO: remove IS_DEV check
  if (UsersUtils.hasPrimaryRoles(user) && IS_DEV) {
    routes.push({
      path: '/auctions',
      menuLabel: txc.auctions,
      menuIcon: icons.bookings,
      component: AuctionsPage,
      exact: true,
    });
  }

  // Guests
  if (
    UsersUtils.hasRoles(
      user,
      UserRole.ADMIN,
      UserRole.CUSTOMER_CARE,
      UserRole.CITY_MANAGER,
      UserRole.RECEIVER
    )
  ) {
    routes.push({
      path: '/guests',
      menuLabel: tx.guests,
      menuIcon: icons.guests,
      component: GuestsPage,
      exact: true,
    });
  }

  // Users
  if (UsersUtils.hasRoles(user, UserRole.ADMIN, UserRole.CUSTOMER_CARE, UserRole.CITY_MANAGER)) {
    routes.push({
      path: '/users',
      menuLabel: tx.users,
      menuIcon: icons.users,
      component: UsersPage,
      exact: true,
    });
  }

  // Tasks
  // if (UsersUtils.hasRoles(user, ...RolesUtils.values)) {
  //   routes.push({
  //     path: '/tasks',
  //     menuLabel: tx.tasks,
  //     menuIcon: AssignmentOutlined,
  //     component: TasksPage,
  //     exact: true,
  //   });
  // }

  // CRM
  if (
    UsersUtils.hasRoles(
      user,
      UserRole.ADMIN,
      UserRole.CUSTOMER_CARE
      // UserRole.CITY_MANAGER,
    )
  ) {
    routes.push({
      path: '/customers',
      menuLabel: 'CRM',
      menuIcon: icons.customers,
      component: CustomersPage,
      exact: true,
    });
  }

  // Requests
  // if (
  //   UsersUtils.hasRoles(user, UserRole.ADMIN, UserRole.CUSTOMER_CARE, UserRole.CITY_MANAGER) &&
  //   user?.email === TEST_DATA.email
  // ) {
  //   routes.push({
  //     path: '/booking-requests',
  //     menuLabel: 'Booking Requests',
  //     menuIcon: SupervisedUserCircleOutlined,
  //     component: RequestsPage,
  //     exact: true,
  //   });
  // }

  return routes;
}
